import React, { useEffect, useRef } from 'react';
import { trackQRLinkClick } from '../utils/analytics';

const LINKS = [
  {
    href: 'https://gethousingai.com',
    icon: 'home',
    color: 'blue',
    title: 'HousingAI',
    desc: 'Learn about our platform',
  },
  {
    href: 'https://www.salemha.org',
    icon: 'chat',
    color: 'green',
    title: 'Live Chatbot Demo',
    desc: 'See it in action on Salem HA',
  },
  {
    href: 'https://gethousingai.com/translate',
    icon: 'translate',
    color: 'purple',
    title: 'Translation Library',
    desc: 'Documents in 10+ languages, updated nightly',
  },
  {
    href: 'https://gethousingai.com/utility-allowances',
    icon: 'zap',
    color: 'amber',
    title: 'Utility Allowance Schedules',
    desc: 'HUD-52667 schedules, every figure cited',
  },
  {
    href: 'https://www.linkedin.com/company/gethousingai/',
    icon: 'linkedin',
    color: 'cyan',
    title: 'LinkedIn',
    desc: 'Follow us for updates',
  },
];

const icons = {
  home: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
  ),
  chat: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
  ),
  translate: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 8l6 6"/><path d="M4 14l6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="M22 22l-5-10-5 10"/><path d="M14 18h6"/></svg>
  ),
  calc: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="16" y1="14" x2="16" y2="18"/><line x1="8" y1="10" x2="8" y2="10"/><line x1="12" y1="10" x2="12" y2="10"/><line x1="16" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="8" y2="14"/><line x1="12" y1="14" x2="12" y2="14"/><line x1="8" y1="18" x2="8" y2="18"/><line x1="12" y1="18" x2="12" y2="18"/></svg>
  ),
  zap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
  ),
  chevron: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
  ),
};

const QrPage = () => {
  const canvasRef = useRef(null);
  const emailRef = useRef(null);

  // Email obfuscation
  useEffect(() => {
    if (emailRef.current) {
      const a = 'sales';
      const b = 'gethousingai.com';
      emailRef.current.href = 'mai' + 'lto:' + a + '@' + b;
      emailRef.current.textContent = a + '@' + b;
    }
  }, []);

  // Neural network canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let nodes = [];
    let w, h, dpr;
    let animId;

    const connectionDist = 140;

    function initNodes() {
      const count = Math.floor((w * h) / (dpr * dpr * 18000));
      nodes = [];
      for (let i = 0; i < count; i++) {
        nodes.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: (Math.random() * 2 + 1.5) * dpr,
          vx: (Math.random() - 0.5) * 0.3 * dpr,
          vy: (Math.random() - 0.5) * 0.3 * dpr,
          phase: Math.random() * Math.PI * 2,
        });
      }
    }

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.width = window.innerWidth * dpr;
      h = canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      initNodes();
    }

    function draw(time) {
      ctx.clearRect(0, 0, w, h);
      const cd = connectionDist * dpr;
      const cd2 = cd * cd;

      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      }

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const d2 = dx * dx + dy * dy;
          if (d2 < cd2) {
            const alpha = (1 - Math.sqrt(d2) / cd) * 0.2;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(45, 105, 236, ${alpha})`;
            ctx.lineWidth = 1.0 * dpr;
            ctx.stroke();
          }
        }
      }

      const t = time * 0.001;
      for (const n of nodes) {
        const pulse = 0.6 + 0.4 * Math.sin(t + n.phase);
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(45, 105, 236, ${0.06 * pulse})`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(45, 105, 236, ${0.35 * pulse})`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${0.3 * pulse})`;
        ctx.fill();
      }

      animId = requestAnimationFrame(draw);
    }

    window.addEventListener('resize', resize);
    resize();
    animId = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap');

        .qr-page {
          font-family: 'DM Sans', -apple-system, sans-serif;
          background: #0B1120;
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          align-items: center;
          overflow-x: hidden;
          color: #FFFFFF;
          position: relative;
        }

        .qr-page * { box-sizing: border-box; }

        .qr-canvas {
          position: fixed;
          top: 0; left: 0;
          width: 100%; height: 100%;
          z-index: 0;
          pointer-events: none;
        }

        .qr-container {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 420px;
          padding: 48px 24px 32px;
          display: flex;
          flex-direction: column;
          align-items: center;
          min-height: 100dvh;
        }

        .qr-logo-mark {
          width: 72px;
          height: 72px;
          border-radius: 20px;
          background: linear-gradient(135deg, rgba(45,105,236,0.15), rgba(45,105,236,0.05));
          border: 1.5px solid rgba(45,105,236,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          backdrop-filter: blur(12px);
          animation: qrLogoIn 0.6s ease-out both;
        }

        .qr-logo-mark svg { width: 44px; height: 44px; }

        .qr-brand-name {
          font-size: 28px;
          font-weight: 700;
          letter-spacing: -0.5px;
          margin-bottom: 6px;
          animation: qrFadeUp 0.6s ease-out 0.1s both;
        }

        .qr-brand-name span { color: #4D94FF; }

        .qr-tagline {
          font-family: 'Space Mono', monospace;
          font-size: 11px;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: #94A3B8;
          margin-bottom: 36px;
          animation: qrFadeUp 0.6s ease-out 0.15s both;
        }

        .qr-links {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 40px;
        }

        .qr-link-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px 20px;
          background: linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02));
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 16px;
          text-decoration: none;
          color: #FFFFFF;
          transition: all 0.25s ease;
          backdrop-filter: blur(8px);
          position: relative;
          overflow: hidden;
        }

        .qr-link-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 100%;
          background: linear-gradient(135deg, rgba(45,105,236,0.1), transparent);
          opacity: 0;
          transition: opacity 0.25s ease;
        }

        .qr-link-card:hover,
        .qr-link-card:active {
          border-color: rgba(45,105,236,0.4);
          transform: translateY(-2px);
          box-shadow: 0 8px 32px rgba(45,105,236,0.15);
        }

        .qr-link-card:hover::before,
        .qr-link-card:active::before { opacity: 1; }

        .qr-link-card:nth-child(1) { animation: qrCardIn 0.5s ease-out 0.25s both; }
        .qr-link-card:nth-child(2) { animation: qrCardIn 0.5s ease-out 0.33s both; }
        .qr-link-card:nth-child(3) { animation: qrCardIn 0.5s ease-out 0.41s both; }
        .qr-link-card:nth-child(4) { animation: qrCardIn 0.5s ease-out 0.49s both; }
        .qr-link-card:nth-child(5) { animation: qrCardIn 0.5s ease-out 0.57s both; }

        .qr-link-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          position: relative;
          z-index: 1;
        }

        .qr-link-icon svg { width: 22px; height: 22px; }

        .qr-link-icon.blue { background: rgba(45,105,236,0.15); color: #4D94FF; }
        .qr-link-icon.green { background: rgba(34,197,94,0.15); color: #4ade80; }
        .qr-link-icon.purple { background: rgba(168,85,247,0.15); color: #c084fc; }
        .qr-link-icon.amber { background: rgba(245,158,11,0.15); color: #fbbf24; }
        .qr-link-icon.cyan { background: rgba(6,182,212,0.15); color: #22d3ee; }

        .qr-link-text { position: relative; z-index: 1; flex: 1; }
        .qr-link-text .title { font-size: 15px; font-weight: 600; margin-bottom: 2px; }
        .qr-link-text .desc { font-size: 12px; color: #94A3B8; line-height: 1.4; }

        .qr-link-arrow {
          position: relative;
          z-index: 1;
          color: #64748B;
          transition: color 0.25s, transform 0.25s;
        }

        .qr-link-card:hover .qr-link-arrow {
          color: #4D94FF;
          transform: translateX(3px);
        }

        .qr-footer {
          margin-top: auto;
          text-align: center;
          animation: qrFadeUp 0.6s ease-out 0.7s both;
        }

        .qr-footer-contact { font-size: 13px; color: #94A3B8; margin-bottom: 6px; }
        .qr-footer-contact a { color: #4D94FF; text-decoration: none; }
        .qr-footer-copy { font-size: 11px; color: #94a3b8; }

        @keyframes qrLogoIn {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes qrFadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes qrCardIn {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div className="qr-page">
        <canvas ref={canvasRef} className="qr-canvas" />

        <div className="qr-container">
          <div className="qr-logo-mark">
            <svg viewBox="3.16 3.96 17.68 17.68" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g stroke="#2D69EC" strokeWidth="1.68" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4.8 20 L4.8 12.2"/>
                <path d="M19.2 12.2 L19.2 20"/>
                <path d="M4.8 20 L12 20"/>
                <path d="M12 5.6 L4 12.8"/>
                <path d="M12 5.6 L20 12.8"/>
              </g>
              <text x="12" y="18.4" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="9.6" fill="#FFFFFF">AI</text>
            </svg>
          </div>

          <h1 className="qr-brand-name" style={{ margin: 0 }}>Housing<span>AI</span></h1>
          <div className="qr-tagline">Multilingual AI for Public Housing</div>

          <div className="qr-links">
            {LINKS.map((link, i) => (
              <a key={i} href={link.href} target="_blank" rel="noopener noreferrer" className="qr-link-card"
                onClick={() => trackQRLinkClick(link.title, link.href)}
              >
                <div className={`qr-link-icon ${link.color}`}>{icons[link.icon]}</div>
                <div className="qr-link-text">
                  <div className="title">{link.title}</div>
                  <div className="desc">{link.desc}</div>
                </div>
                <div className="qr-link-arrow">{icons.chevron}</div>
              </a>
            ))}
          </div>

          <div className="qr-footer">
            <div className="qr-footer-contact">
              <a ref={emailRef} href="#"> </a>
            </div>
            <div className="qr-footer-copy">&copy; 2026 HousingAI, LLC</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QrPage;